<template>
	<page-container :page="page">
		<heading-image
			v-if="page && page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>

		<main-content
			:title="page.title || page.header"
			:title-right="page.titleRight"
			:subtitle="page.subtitle"
			:content="page.content"
			class="packages-wrapper"
		>
			<template #rightContent>
				<div v-if="page.introImage" class="columns column6 intro-images">
					<div class="intro-square">
						<picture>
							<source v-if="page.introImageWebp" :srcset="page.introImageWebp" type="image/webp" />
							<source :srcset="page.introImage" />
							<img :src="page.introImage" />
						</picture>
					</div>
					<div v-if="page.introImagePortrait" class="intro-portrait">
						<picture>
							<source
								v-if="page.introImagePortraitWebp"
								:srcset="page.introImagePortraitWebp"
								type="image/webp"
							/>
							<source :srcset="page.introImagePortrait" />
							<img :src="page.introImagePortrait" />
						</picture>
					</div>
				</div>
			</template>
		</main-content>

		<section class="main-content background-dark">
			<alternating-block v-for="block in packagesData" :key="block.ID" class="row block-item">
				<div class="columns column6">
					<nuxt-link :to="localePath(`/${block.filename}`)">
						<picture>
							<source :srcset="block.imageOverviewWebp" type="image/webp" />
							<source :srcset="block.imageOverview" />
							<img
								class="object-fit"
								:src="block.imageOverview"
								:alt="block.imageOverviewAlt"
								loading="lazy"
							/>
						</picture>
					</nuxt-link>
				</div>
				<div class="columns column6 col-content">
					<div class="content">
						<h3>
							<nuxt-link :to="localePath(`/${block.filename}`)">
								{{ block.title || block.header }}
							</nuxt-link>
						</h3>
						<div>
							<p>{{ block.contentShort }}</p>
						</div>
						<button-container>
							<nuxt-link :to="localePath(`/${block.filename}`)" class="button-arrow">
								{{ defaults[locale].packages.view }}
							</nuxt-link>
							<book-button v-if="block.bookingID" :packageid="block.bookingID" class="button">
								{{ defaults[locale].packages.book }}
							</book-button>
						</button-container>
					</div>
				</div>
			</alternating-block>
		</section>

		<contentblocks v-if="page.contentblocks && page.contentblocks.length" :items="page.contentblocks" />

		<review-slider v-if="page.showReviewSlider" :header="defaults[locale].website.reviewsHeader" />

		<frequently-asked-questions
			v-if="page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>

		<newsletter
			v-if="page.showNewsletter"
			:header="defaults[locale].newsletter.title"
			:content="defaults[locale].newsletter.content"
			:button="defaults[locale].newsletter.button"
		/>
	</page-container>
</template>

<script setup>
import Rellax from 'rellax';

const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();
await fetchDefaults();

const { data: packagesData } = await useWebsiteFetch('packages', {
	query: { language: locale.value },
	key: `${locale.value}/packages`,
});

onMounted(() => {
	new Rellax('.intro-square', {
		speed: -1,
		center: true,
		wrapper: null,
		round: true,
		vertical: true,
		horizontal: false,
	});
	new Rellax('.intro-portrait', {
		speed: 1,
		center: true,
		wrapper: null,
		round: true,
		vertical: true,
		horizontal: false,
	});
});
</script>

<style lang="scss" scoped>
.packages-wrapper.main-content#intro {
	padding: 75px 0 50px;
}

.col-content {
	padding: 0;
	display: flex;
	flex-flow: row wrap;
	justify-content: center;
	align-items: stretch;
}

.intro-images {
	display: flex;
	flex-flow: row wrap;
	justify-content: space-between;
	align-items: flex-start;

	div {
		width: 48%;

		&:first-child {
			margin-top: 110px;
		}
	}
}

@media (max-width: 960px) {
	.packages-wrapper.main-content#intro {
		padding: 50px 0 30px;
	}

	.col-content {
		padding: 0 20px;
	}

	.intro-images {
		div {
			width: 47%;

			&:first-child {
				margin-top: 60px;
			}
		}
	}
}
</style>
